import React, {useEffect, useRef} from 'react';
import clsx from 'clsx';
import {Icon} from '@netx/core-icons';
import {faTimesCircle} from '@netx/core-icons/solid';
import {alpha} from '@mui/system';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {Tooltip} from '@netx/core-components/Tooltip';
import {i18n} from '@netx/core/i18n';
import {
	CHIP_DELETE_ICON_CLASS_NAME,
	chipDeleteIconClasses,
	type ChipDeleteIconClasses,
	getChipDeleteIconUtilityClass,
} from './chipDeleteIconClasses';

export type ChipRef = React.ForwardedRef<HTMLDivElement>;

export type ChipDeleteIconOwnerState = WithClasses<ChipDeleteIconClasses>;

const useUtilityClasses = (props: ChipDeleteIconOwnerState) => {
	const {classes} = props;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getChipDeleteIconUtilityClass, classes);
};

const ChipDeleteIconRoot = styled('div', {
	name: CHIP_DELETE_ICON_CLASS_NAME,
	slot: 'Root',
})(({theme}) => {
	const deleteIconColor = alpha(theme.palette.text.primary, 0.26);
	return {
		// Adjust for font-awesome
		height: `20px !important`,
		fontSize: `20px !important`,
		lineHeight: 1,
		// Make focusable
		'&:hover, &:focus': {
			color: alpha(deleteIconColor, 0.99),
			outline: 'none',
			outlineStyle: 'none',
			boxShadow: 'none',
			borderColor: 'transparent',
		},
	};
});

export interface ChipDeleteIconProps
	extends
		React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>,
		WithClasses<ChipDeleteIconClasses> {
	tabbable?: boolean;
	disabled?: boolean;
	focused?: boolean;
	title?: string;
	onTooltipToggle?: (open: boolean) => void;
}

export const ChipDeleteIconPrimitive = (
	{
		classes: _classes,
		className,
		disabled = false,
		focused,
		tabbable,
		title,
		onTooltipToggle,
		...props
	}: ChipDeleteIconProps,
	ref: ChipRef,
) => {
	const innerRef = useSharedRef<HTMLDivElement | null>(null, [ref]);
	const deleteIconRef = useRef<HTMLDivElement>(null);

	const ownerState: ChipDeleteIconOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	useEffect(() => {
		if (!focused || tabbable) return;
		innerRef.current?.focus();
	});

	const handleDeleteIconKeydown = useConstCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (disabled) return;
			switch (event.key) {
				case 'Enter':
				case ' ':
					event.preventDefault();
					event.stopPropagation();
					deleteIconRef.current?.click();
					break;
			}
		},
	);

	return (
		<Tooltip
			title={title !== '' && title != null ? title : i18n.t('i18n.remove')}
			TransitionProps={{
				onEntering: () => onTooltipToggle?.(true),
				onExited: () => onTooltipToggle?.(false),
			}}
		>
			<ChipDeleteIconRoot
				className={clsx(classes.root, className)}
				onKeyDown={handleDeleteIconKeydown}
				ref={deleteIconRef}
				role="button"
				tabIndex={tabbable || focused ? 0 : -1}
				{...props}
			>
				<Icon icon={faTimesCircle} fixedWidth />
			</ChipDeleteIconRoot>
		</Tooltip>
	);
};

export const ChipDeleteIcon = React.forwardRef(ChipDeleteIconPrimitive) as (
	props: ChipDeleteIconProps,
) => JSX.Element;

export default ChipDeleteIcon;
